/**
 * Client-side PII scrubbing. Runs on every error message
 * before the event ever leaves the browser.
 *
 * The patterns and length caps are intentionally identical to the server-side
 * `Api/Telemetry.Endpoints/Services/PiiScrubber.cs`. Defence in depth: the
 * server re-runs the same patterns as a backstop.
 *
 * Pattern order matters - most specific first. OneDrive URLs are matched
 * before the SharePoint pattern (so personal `-my.sharepoint.com` sites win),
 * and both are matched before the generic-URL pattern so their tokens win
 * over "keep the path, drop the query".
 */

/** Cap for human-readable messages (error.scrubbedMessage etc). */
export const MAX_MESSAGE_LENGTH = 500;

interface ScrubPattern {
  readonly pattern: RegExp;
  readonly replacement: string;
}

/**
 * Ordered scrub patterns - most specific first.
 *
 * ReDoS hardening (Sonar typescript:S5852): every quantifier has an explicit
 * upper bound. JavaScript regexes don't support timeouts, so unbounded `+` /
 * `*` quantifiers over permissive character classes are a denial-of-service
 * hazard on adversarial input (catastrophic backtracking).
 *
 * The bounds are well above real-world values for each pattern type:
 *   - Email local part: 64 (RFC 5321 maximum).
 *   - Email domain:     255 (RFC 5321 maximum).
 *   - Everything else:  <= 500, which matches MAX_MESSAGE_LENGTH so a single
 *                       match can never exceed the post-scrub length cap.
 * Legitimate matches are unaffected; pathological inputs degrade to "no
 * match" or "partial match" instead of pinning the JS thread.
 */
const PATTERNS: readonly ScrubPattern[] = [
  // Email addresses - bounded to RFC 5321 limits
  { pattern: /[\w.+-]{1,64}@[\w-]{1,255}\.[\w.-]{1,255}/g, replacement: "<email>" },

  // OneDrive URLs (personal storage) - matched before the generic SharePoint
  // pattern so personal `<tenant>-my.sharepoint.com` sites resolve to
  // <onedrive-url> rather than <sharepoint-url>. Also covers the consumer
  // OneDrive hosts `onedrive.live.com` and the `1drv.ms` short links.
  {
    pattern:
      /https:\/\/(?:[^/\s]{1,200}-my\.sharepoint\.com|onedrive\.live\.com|1drv\.ms)(?:\/[^\s]{0,500})?/gi,
    replacement: "<onedrive-url>",
  },

  // SharePoint (team/site) URLs (most-specific URL pattern first)
  {
    pattern: /https:\/\/[^/\s]{1,200}\.sharepoint\.com\/[^\s]{1,500}/gi,
    replacement: "<sharepoint-url>",
  },

  // Generic URLs with query strings - keep the path, drop the parameters
  { pattern: /(https?:\/\/[^\s?#]{1,500})[?#][^\s]{0,500}/g, replacement: "$1" },

  // UUIDs / GUIDs in any common shape (with or without dashes) - already bounded
  {
    pattern:
      /\b[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}\b/g,
    replacement: "<id>",
  },

  // Long alphanumeric IDs / tokens (20 to 500 chars)
  { pattern: /\b[A-Za-z0-9_-]{20,500}\b/g, replacement: "<token>" },

  // Windows file paths
  { pattern: /[A-Za-z]:\\[^\s]{1,500}/g, replacement: "<windows-path>" },

  // macOS user-home paths
  { pattern: /\/Users\/[^\s/]{1,200}\/[^\s]{0,500}/g, replacement: "<mac-path>" },

  // Anything in double or single quotes (4 to 500 chars - filenames in errors)
  { pattern: /"[^"]{4,500}"/g, replacement: "<quoted>" },
  { pattern: /'[^']{4,500}'/g, replacement: "<quoted>" },
];

/**
 * Apply every pattern to a single text value, then enforce the length cap.
 * Null / empty / undefined input returns an empty string so callers can
 * `String(x)` the result without a null check.
 */
export function scrubMessage(input: string | undefined | null): string {
  if (input === undefined || input === null || input === "") {
    return "";
  }
  let current = input;
  for (const { pattern, replacement } of PATTERNS) {
    current = current.replace(pattern, replacement);
  }
  return truncate(current, MAX_MESSAGE_LENGTH);
}

/** Truncate `input` to `max` chars; append "..." marker when shortened. */
function truncate(input: string, max: number): string {
  if (input.length <= max) return input;
  return input.substring(0, max - 3) + "...";
}
